import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, DateInput, Position, SaveCancelControls, Text, useResponsive } from '@common-fe/common-fe';
import dayjs from 'dayjs';

import { DEFAULT_DATE_FORMAT } from '@/common/constants';
import { EditableBadge, WarningMessage } from '@/modules/UseMyMoney/components';
import { useMyMoneyFormStore } from '@/modules/UseMyMoney/stores';
import { useChangeDateOfService, useDateOfServiceOptions } from '@/modules/UseMyMoney/widgets/FieldSetup/hooks';

import FieldSetupLabel from '../FieldSetupLabel/FieldSetupLabel';
import FieldSetupOptions from '../FieldSetupOptions/FieldSetupOptions';

interface Props {
  hasAvailableAccounts?: boolean;
}

const DateOfServiceSetup: React.FC<Props> = ({ hasAvailableAccounts }) => {
  const { t } = useTranslation();
  const { isMobile } = useResponsive();
  const { values } = useMyMoneyFormStore();
  const [dateOfService, setDateOfService] = useState(values?.customDateOfService || '');

  const {
    suggestedDateOfServiceOptions,
    highlightedDates,
    selectedDateFromSuggested,
  } = useDateOfServiceOptions({ dateOfService });

  const {
    isEditMode,
    errorText,
    setIsEditMode,
    handleChangeDateInput,
    handleChangeSuggestedDateOfService,
    handleChangeDateOfService,
    handleSave,
    handleCancel,
  } = useChangeDateOfService({
    dateOfService,
    setDateOfService,
    isEditModeByDefault: !!selectedDateFromSuggested
  });

  const isInvalidDate = useMemo(() => !hasAvailableAccounts && values?.dateOfService === values?.customDateOfService,
    [hasAvailableAccounts, values]);

  return (
    <Box data-testId="date-of-service-setup">
      <FieldSetupLabel
        testId="date-of-service"
        hasValue={!!values?.dateOfService}
        title={t('When was this expense?')}
        subTitle={t('Date entered should be the date the expense incurred. This may differ from the payment date.')}
        isMobile={isMobile}
      />

      {suggestedDateOfServiceOptions?.length ? (
        <Box margin={{ top: 'spacing24' }}>
          <Text weight={700}>
            {suggestedDateOfServiceOptions?.length > 1 
              ? t('Dates found on your document')
              : t('Date found on your document')}
          </Text>
          <FieldSetupOptions
            testId="suggested-date-of-service"
            margin={{ top: 'spacing8' }}
            value={values?.dateOfService}
            onChangeValue={handleChangeSuggestedDateOfService}
            options={suggestedDateOfServiceOptions}
            style={{ flexWrap: 'wrap' }}
          />
        </Box>
      ) : null}

      {/* Custom date of service setup */}
      <Box margin={{ top: 'spacing24' }}>
        <Text weight={700}>
          {t('Not right? Enter or select a new one')} 
        </Text>
        <Box margin={{ top: 'spacing8' }} align="start">
          <EditableBadge
            isEditMode={isEditMode || isInvalidDate}
            setIsEditMode={setIsEditMode}
            isSelected={values?.dateOfService === values?.customDateOfService}
            value={values?.customDateOfService
              ? dayjs(values?.customDateOfService).format(DEFAULT_DATE_FORMAT)
              : undefined}
            title={values?.customDateOfService
              ? dayjs(values?.customDateOfService).format(DEFAULT_DATE_FORMAT)
              : undefined}
            onChangeValue={handleChangeDateOfService}
          >
            <Box direction="row" gap="spacing12" width={isMobile ? '100%' : undefined}>
              <Box width={isMobile ? '100%' : '160px'}>
                <SaveCancelControls
                  testId="date-of-service"
                  onSave={handleSave}
                  onCancel={handleCancel}
                >
                  <DateInput
                    testId="date-of-service"
                    name="date-of-service"
                    maxDate={dayjs()}
                    value={dateOfService}
                    onChange={handleChangeDateInput}
                    placeholder="MM/DD/YYYY"
                    onApply={handleSave}
                    highlightedDates={highlightedDates}
                    errorText={errorText}
                    isError={isInvalidDate}
                  />
                </SaveCancelControls>
              </Box>
              <DateInput
                testId="date-of-service"
                name="date-of-service"
                value={dateOfService}
                onChange={handleChangeDateInput}
                onApply={handleChangeDateOfService}
                maxDate={dayjs()}
                placeholder="MM/DD/YYYY"
                pickerSide={isMobile ? Position.BOTTOM : Position.RIGHT}
                isButtonMode
              />
            </Box>
          </EditableBadge>
        </Box>
      </Box>

      {hasAvailableAccounts ? null : (
        <WarningMessage
          testId="have-no-plans"
          margin={{ top: 'spacing24' }}
          title={t('You have no plans to cover this date of service.')}
        />
      )}
    </Box>
  );
};

export default DateOfServiceSetup;
